import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { LuSettings } from 'react-icons/lu';

import { MODULE_CONFIG } from '../components/Menu/ModuleContext';
import { MODULES_NAV, GROUP_CONFIGURACOES } from '../components/Menu/MenuItems/nav';

/**
 * Árvore de navegação filtrada por permissão, no contrato do handoff de design
 * da home (§3): `módulo → grupos → itens`.
 *
 * Fonte única: os MESMOS catálogos que alimentam a sidebar (MODULE_CONFIG,
 * MODULES_NAV, GROUP_CONFIGURACOES). A home não mantém lista própria de telas.
 *
 * Regras de visibilidade espelhadas da sidebar (MenuItems.jsx):
 *   - módulo aparece se o usuário tem QUALQUER uma das requiredPermissions;
 *   - topItem aparece por `permission` (única, obrigatória);
 *   - child de grupo aparece por `permissionAny` (array vazio = sem restrição).
 *
 * §3.1 do handoff: o filtro é feito ANTES de renderizar — grupo que ficou sem
 * itens não é devolvido, e módulo que ficou sem grupos não vira card. A busca
 * opera sobre esta árvore já filtrada.
 *
 * @typedef {Object} CatalogItem
 * @property {string} to
 * @property {string} label
 * @property {React.ComponentType} icon - Ícone do catálogo (react-icons/fa)
 * @property {boolean} novo
 *
 * @typedef {Object} CatalogGroup
 * @property {string} nome
 * @property {CatalogItem[]} itens
 *
 * @typedef {Object} CatalogModule
 * @property {string} id - Chave do MODULE_CONFIG (slug estável)
 * @property {string} nome
 * @property {React.ComponentType} icon - Ícone Lucide do card
 * @property {CatalogGroup[]} grupos
 * @property {boolean} hasNew - Algum item do módulo tem `novo: true`
 * @property {boolean} isModule - false p/ Configurações (não muda módulo ativo)
 *
 * @returns {{ modules: CatalogModule[], flatItems: Array }}
 */
export default function useModuleCatalog() {
   const userPermissions = useSelector((state) => state.auth.user?.permissions);

   const permissionsSet = useMemo(
      () => new Set(userPermissions || []),
      [userPermissions]
   );

   return useMemo(() => {
      const has = (key) => permissionsSet.has(key);
      // Array vazio/undefined = sem restrição (mesma semântica do hasAnyPermission)
      const hasAny = (keys = []) => !keys.length || keys.some(has);

      const toItem = (entry) => ({
         to: entry.to,
         label: entry.label,
         icon: entry.icon,
         novo: !!entry.newItem,
      });

      // Devolve os grupos JÁ filtrados; grupo vazio não sobrevive (§3.1).
      const collectGroups = (nav) => {
         if (!nav) return [];

         const groups = [];

         // topItems não pertencem a nenhum grupo declarado — viram um bloco
         // sem rótulo, renderizado antes dos demais.
         const topItems = (nav.topItems || [])
            .filter((item) => has(item.permission))
            .map(toItem);

         if (topItems.length) groups.push({ nome: null, itens: topItems });

         (nav.groups || []).forEach((group) => {
            const itens = group.children
               .filter((child) => hasAny(child.permissionAny))
               .map(toItem);

            if (itens.length) groups.push({ nome: group.label, itens });
         });

         return groups;
      };

      const buildModule = ({ id, nome, icon, nav, isModule }) => {
         const grupos = collectGroups(nav);
         if (!grupos.length) return null; // §3.1 — sem grupos, não vira card

         return {
            id,
            nome,
            icon,
            grupos,
            hasNew: grupos.some((g) => g.itens.some((i) => i.novo)),
            isModule,
         };
      };

      // Ordem de exibição = ordem de declaração do MODULE_CONFIG (mesma do
      // seletor de módulo da sidebar).
      const modules = Object.entries(MODULE_CONFIG)
         .filter(([, cfg]) => cfg.requiredPermissions.some(has))
         .map(([key, cfg]) =>
            buildModule({
               id: key,
               nome: cfg.label,
               icon: cfg.iconComponent,
               nav: MODULES_NAV[key],
               isModule: true,
            })
         )
         .filter(Boolean);

      // Configurações não é módulo (não entra no MODULE_CONFIG nem no seletor),
      // mas tem a mesma forma de card na home. `isModule: false` impede que
      // abrir seus itens troque o módulo ativo da sidebar.
      const settings = hasAny(GROUP_CONFIGURACOES.requiredAny)
         ? buildModule({
              id: GROUP_CONFIGURACOES.key,
              nome: GROUP_CONFIGURACOES.label,
              icon: LuSettings,
              nav: { groups: [GROUP_CONFIGURACOES] },
              isModule: false,
           })
         : null;

      const allModules = settings ? [...modules, settings] : modules;

      // Projeção plana para a busca (§2.3): carrega o caminho `Módulo › Grupo`.
      const flatItems = allModules.flatMap((mod) =>
         mod.grupos.flatMap((grupo) =>
            grupo.itens.map((item) => ({
               ...item,
               moduleId: mod.id,
               moduleName: mod.nome,
               groupName: grupo.nome,
               isModule: mod.isModule,
            }))
         )
      );

      return { modules: allModules, flatItems };
   }, [permissionsSet]);
}
